import { gql } from "@apollo/client";

import {
  ITEMBASICSFRAGMENT,
  REVIEWFEEDFRAGMENT,
  FEATUREDIMAGETHUMBNAILFRAGMENT,
  RELATEDPRODUCTNODEFRAGMENT,
  LIMITEDITEMBASICSFRAGMENT,
} from "@geerly/shared";

import { REVIEWCONTENTFRAGMENT } from "components/Reviews/Reviews";

import {
  FEATUREDIMAGEFRAGMENT,
  IMAGECONTENTLARGEANDTHUMBNAILFRAGMENT,
} from "components/Image/FeaturedImage";
import { SEOFRAGMENT } from "seo-modules/PageSeo";
import {
  PRODUCTATHLETEFRAGMENT,
  PRODUCTATHLETESFEEDFRAGMENT,
} from "components/ProductAthletes/ProductAthletes";

import { FEEDBLOCKFRAGMENT } from "components/Feedblocks/FeedBlock";
import { RELATEDTERMSCONTENTFRAGMENT } from "components/ProductContent/TermSpecificContent";

export const PRODUCTHIERARCHYFRAGMENT = gql`
  fragment ProductHierarchyFragment on Product_Productdetails {
    baseModel {
      ... on Product {
        databaseId
        id
        uri
        title
        noBrandTitle
      }
    }
  }
`;

const ATTRIBUTETYPEFRAGMENT = gql`
  fragment AttributeTypeFragment on AttributeType {
    id
    databaseId
    slug
    name
  }
`;

const RELATEDPRODUCTSFRAGMENT = gql`
  fragment RelatedProductsFragment on ProductNode {
    ... on ContentNode {
      databaseId
      ...ItemBasicsFragment
      ...FeedBlockFragment
    }
    ... on Family {
      featuredImage {
        node {
          altText
          caption
          thumbnail: sourceUrl(size: THUMBNAIL)
          id
        }
      }
    }
    ... on Product {
      featuredImage {
        node {
          ...ImageContentLargeAndThumbnailFragment
        }
      }
      specialVersions {
        nodes {
          slug
          id
        }
      }
      productDetails {
        released
        #baseModel {
        #   ... on Product {
        #     id
        #   }
        # }
      }
    }
    ... on ProductNode {
      ...ReviewFeedFragment
      ...ProductAthletesFeedFragment
      priceBand {
        node {
          slug
          id
        }
      }
      specifications(location: COMPARISON_BLOCK) {
        edges {
          # Not actually needed here;
          #  attributeType {
          #    ...AttributeTypeFragment
          #  }
          specifications {
            content {
              value
              tooltip
            }
            specification {
              ... on Attribute {
                id
                title
              }
            }
          }
        }
      }
      characteristics(location: COMPARISON_BLOCK, language: $lang) {
        edges {
          characteristic {
            uri
            node {
              label
              name
              id
            }
          }
          terms {
            edges {
              uri
              node {
                __typename
                ... on TermNode {
                  id
                  slug
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`;

const SIMILARPRODUCTSFRAGMENT = gql`
  fragment SimilarProductsFragment on SimilarProductsTermsObject {
    terms {
      edges {
        node {
          __typename
          ... on TermNode {
            slug
            name
            id
          }
          ... on TermContentNode {
            safeLowerCasedName
            characteristic {
              node {
                name
                id
              }
            }
          }
        }
        uri
        productNodes {
          nodes {
            similarityWeighting
            node {
              ...RelatedProductsFragment
            }
          }
        }
        hasMoreResults
      }
    }
  }
`;

const FAMILYRELATEDPRODUCTSFRAGMENT = gql`
  fragment FamilyRelatedProductsFragment on Family {
    ...ItemBasicsFragment
    uri
    products(first: $first, last: $last, offset: $offset, currentSlug: $slug) {
      nodes {
        ...RelatedProductsFragment
        ... on Product {
          latestModelDetails {
            isLatestModel
          }
          noBrandTitle #only needed on Family products
        }
      }
      pageInfo {
        hasPreviousPage
        offsetPagination {
          total
        }
      }
    }
  }
`;

// TODO: need to roll all the Product specific elements into itemBasics, or create that as a template, to be reused in the Revisions query
// ${revisionQuery}

export const PRODUCT_QUERY = gql`
  query Product(
    $id: ID!
    $idType: ContentNodeIdTypeEnum!
    $asPreview: Boolean
    $lang: LanguageCodeFilterEnum!
    $postFeedItemsToShow: Int!
    $comparisonItemsToShow: Int!
    $similarProductsTermLimit: Int!
    $first: Int
    $last: Int
    $offset: Int
    #   $firstComparison: Int
    #    $lastComparison: Int
    #    $before: String
    #    $after: String
    $slug: ID
  ) {
    product: contentNode(id: $id, idType: $idType, asPreview: $asPreview) {
      ... on ContentNode {
        ...ItemBasicsFragment
        ...SeoFragment
      }
      ... on Family {
        featuredImage {
          node {
            ...ImageContentLargeAndThumbnailFragment
            colours {
              nodes {
                id
                databaseId
              }
            }
          }
        }
        familyDetails {
          manufacturerVideos {
            url
            embeddable
            sexes {
              ... on Enum {
                slug
                id
              }
            }
          }
        }
        ...FamilyRelatedProductsFragment
      }
      ... on Product {
        dateGmt
        modifiedGmt
        verifiedColours {
          nodes {
            name
            id
          }
        }
        images {
          ...ImageContentLargeAndThumbnailFragment
        }
        productImages {
          featuredColour {
            id
            databaseId
          }
        }
        latestModelDetails {
          isLatestModel
          latestBaseModel {
            ... on Product {
              id
              slug
              uri
              title
              databaseId
              noBrandTitle
            }
          }
        }

        productNodeContent {
          summary
          oneLiner
          detail
          updates
          verdict
          baseModelSummary
          whoShouldBuy
          whoShouldNotBuy
          goodFor
          faqs {
            answer
            question
          }
          prosCons {
            cons {
              content
            }
            pros {
              content
            }
          }
          inFocus {
            content
            focus {
              ... on Attribute {
                id
                title
              }
            }
          }
          commonlyComparedTo {
            ...RelatedProductNodeFragment
          }
          specialVersionSummaries {
            ...RelatedProductNodeFragment
          }
        }
        specialVersions {
          nodes {
            id
            uri
            title
            noBrandTitle
          }
        }
        productDetails {
          ...ProductHierarchyFragment
          released
        }
        family {
          node {
            ...FamilyRelatedProductsFragment
          }
        }
      }
      ... on ProductNode {
        timeToRead
        hasWrittenContent
        sport {
          node {
            id
            slug
            name
          }
        }
        category {
          uri
          node {
            ...ItemBasicsFragment
            name
            uri
            slug
            categoryDetails {
              singleName
              supportedChoiceTypes
              fallbackImage {
                sourceUrl(size: MEDIUM)
                altText
                id
              }
            }
            specifications(location: COMPARISON_BLOCK) {
              edges {
                specification {
                  ... on Attribute {
                    id
                    title
                  }
                }
              }
            }
            characteristics(location: COMPARISON_BLOCK, language: $lang) {
              edges {
                content
                title
                characteristic {
                  node {
                    id
                    label
                    name
                  }
                }
              }
            }
          }
        }
        brand {
          uri
          node {
            id
            slug
            name
          }
        }
        priceBand {
          node {
            slug
            id
          }
        }
        productAthletes(where: { athleteType: PRO }, first: 12) {
          pageInfo {
            offsetPagination {
              total
            }
          }
          edges {
            node {
              ...ProductAthleteFragment
            }
          }
        }
        ...ReviewContentFragment
        characteristics(
          location: LIST_PAGE
          language: $lang
          hideTermsWithNoProducts: true
        ) {
          edges {
            characteristic {
              uri
              node {
                label
                id
              }
            }
            content
            title
            terms {
              edges {
                uri
                termCount
                node {
                  ... on TermNode {
                    id
                    slug
                    name
                  }
                }
                termContent {
                  ...RelatedTermsContentFragment
                }
              }
            }
          }
        }
        similarProducts(
          limit: $comparisonItemsToShow
          termLimit: $similarProductsTermLimit
          includeSecondaryCharacteristics: true
          productType: INHERIT
          showFeaturedPrimaryTerms: false # Was set to TRUE for running shoes, but prevents showing much on Hiking Boots. Probably now obsolete?
        ) {
          ...SimilarProductsFragment
        }
        specifications(location: PRODUCT_TABLE, restrictPercentileRange: true) {
          edges {
            attributeType {
              ...AttributeTypeFragment
            }
            specifications {
              content {
                value
                tooltip
              }
              percentile {
                value
              }
              specification {
                ... on Attribute {
                  id
                  title
                }
              }
            }
          }
        }
        relatedGuides(first: $postFeedItemsToShow) {
          nodes {
            ...ItemBasicsFragment
            ...FeedBlockFragment
          }
        }
      }
      ... on NodeWithRevisions {
        revisionOf {
          node {
            uri
            id
          }
        }
      }
    }
  }
  ${ITEMBASICSFRAGMENT}
  ${LIMITEDITEMBASICSFRAGMENT}
  ${SEOFRAGMENT}
  ${FEATUREDIMAGEFRAGMENT}
  ${IMAGECONTENTLARGEANDTHUMBNAILFRAGMENT}
  ${FEATUREDIMAGETHUMBNAILFRAGMENT}
  ${FEEDBLOCKFRAGMENT}
  ${REVIEWFEEDFRAGMENT}
  ${PRODUCTATHLETEFRAGMENT}
  ${PRODUCTATHLETESFEEDFRAGMENT}
  ${REVIEWCONTENTFRAGMENT}
  ${RELATEDTERMSCONTENTFRAGMENT}
  ${RELATEDPRODUCTSFRAGMENT}
  ${FAMILYRELATEDPRODUCTSFRAGMENT}
  ${PRODUCTHIERARCHYFRAGMENT}
  ${ATTRIBUTETYPEFRAGMENT}
  ${SIMILARPRODUCTSFRAGMENT}
  ${RELATEDPRODUCTNODEFRAGMENT}
`;

export const UPDATE_FAMILY_PRODUCTS_QUERY = gql`
  # THIS IS A WIP
  query UpdateFamilyProducts(
    $id: ID!
    $first: Int
    $last: Int
    $slug: ID!
    $offset: Int!
    $lang: LanguageCodeFilterEnum!
  ) {
    familyNode: contentNode(id: $id, idType: ID) {
      id
      ... on Family {
        ...FamilyRelatedProductsFragment
      }
    }
  }
  ${RELATEDPRODUCTSFRAGMENT}
  ${FAMILYRELATEDPRODUCTSFRAGMENT}
  ${ITEMBASICSFRAGMENT}
  ${FEEDBLOCKFRAGMENT}
  ${REVIEWFEEDFRAGMENT}
  ${PRODUCTATHLETESFEEDFRAGMENT}
`;

export const PRODUCT_QUERY_PRERENDER = gql`
  query GetContentNodesToPreRender(
    $status: PostStatusEnum!
    $after: String
    $querySize: Int!
    $type: ContentTypeEnum!
    $taxQuery: TaxQuery
  ) {
    contentNodes(
      after: $after
      first: $querySize
      where: { contentTypes: [$type], status: $status, taxQuery: $taxQuery }
    ) {
      pageInfo {
        endCursor
        hasNextPage
      }
      nodes {
        ...ItemBasicsFragment
        ... on ProductNode {
          category {
            node {
              id
            }
          }
          brand {
            node {
              id
            }
          }
        }
        ... on ProductNode {
          sport {
            node {
              id
            }
          }
        }
        ... on Family {
          products {
            nodes {
              ... on Product {
                id
              }
            }
          }
        }
        ... on Athlete {
          athleteProducts {
            pageInfo {
              offsetPagination {
                total
              }
            }
          }
        }
      }
    }
  }
  ${ITEMBASICSFRAGMENT}
`;

export const PRODUCT_PRICE_BAND_QUERY = gql`
  query ProductPriceBandQuery($databaseId: ID!) {
    product(id: $databaseId, idType: DATABASE_ID) {
      id
      priceBand {
        node {
          id
          slug
        }
      }
    }
  }
`;

export const SIMILAR_PRODUCTS_QUERY = gql`
  query SimilarProductsQuery($id: ID!) {
    productNode: contentNode(id: $id, idType: ID) {
      id
      ... on ProductNode {
        similarProducts(
          limit: 10
          termLimit: 10
          includeSecondaryCharacteristics: true
          productType: INHERIT
        ) {
          terms {
            edges {
              node {
                __typename
                ... on TermNode {
                  slug
                  name
                  id
                }
                ... on TermContentNode {
                  safeLowerCasedName
                  characteristic {
                    node {
                      name
                      id
                    }
                  }
                }
              }
              productNodes {
                nodes {
                  similarityWeighting
                  node {
                    ... on ContentNode {
                      databaseId
                      link
                      ...ItemBasicsFragment
                    }
                    ... on Family {
                      featuredImage {
                        node {
                          id
                          altText
                          thumbnail: sourceUrl(size: THUMBNAIL)
                        }
                      }
                    }
                    ... on Product {
                      ...FeaturedImageThumbnailFragment
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${ITEMBASICSFRAGMENT}
  ${FEATUREDIMAGETHUMBNAILFRAGMENT}
`;
/* 
export const PRODUCT_NODE_CONTENT_QUERY = gql`
  query ProductNodeContentQuery($id: ID!) {
    productNode: contentNode(id: $id, idType: ID) {
      id
      ... on ProductNode {
        ...AlertProductFragment
      }
    }
  }
  ${LIMITEDITEMBASICSFRAGMENT}
  ${FEATUREDIMAGETHUMBNAILFRAGMENT}
`;
 */
